import { useEffect, useRef, useState } from "react";

const RETRY_DELAYS = [1500, 4000, 9000];

function bust(src, n) {
  if (!n) return src;
  return `${src}${src.includes("?") ? "&" : "?"}retry=${n}`;
}

/**
 * <img> with a soft shimmer placeholder layered on top until the photo has
 * decoded — tiles never look like a stuck grey block while variants stream in.
 * Failed loads (storage hiccup → 502) retry with backoff and broadcast
 * `lumetier:photo-error` / `lumetier:photo-ok` so the page can show a
 * "photos are reconnecting" banner. `ratio` (w/h) reserves the box for
 * natural-height images (Cinema).
 */
export default function ShimmerImg({ wrapperClassName = "", ratio, src, ...imgProps }) {
  const [loaded, setLoaded] = useState(false);
  const [gone, setGone] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (ref.current?.complete && ref.current.naturalWidth) setLoaded(true);
    return () => clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    if (!loaded) return undefined;
    const t = setTimeout(() => setGone(true), 600);
    return () => clearTimeout(t);
  }, [loaded]);

  // Manual "Retry now" from the status banner re-kicks any still-failed tile.
  useEffect(() => {
    const onRetry = () => { if (!loaded) setAttempt((a) => a + 1); };
    window.addEventListener("lumetier:photo-retry", onRetry);
    return () => window.removeEventListener("lumetier:photo-retry", onRetry);
  }, [loaded]);

  function onError() {
    window.dispatchEvent(new CustomEvent("lumetier:photo-error"));
    if (attempt >= RETRY_DELAYS.length) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setAttempt((a) => a + 1), RETRY_DELAYS[attempt]);
  }

  function onLoad() {
    setLoaded(true);
    if (attempt > 0) window.dispatchEvent(new CustomEvent("lumetier:photo-ok"));
  }

  return (
    <span
      className={`relative block ${wrapperClassName}`}
      style={!loaded && ratio ? { aspectRatio: ratio } : undefined}
    >
      <img ref={ref} {...imgProps} src={bust(src, attempt)} onLoad={onLoad} onError={onError} />
      {!gone && (
        <span
          aria-hidden
          className={`absolute inset-0 pointer-events-none gallery-shimmer transition-opacity duration-500 ${loaded ? "opacity-0" : "opacity-100"}`}
          data-testid="photo-shimmer"
        />
      )}
    </span>
  );
}
